import React, { MutableRefObject } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Markup } from 'react-render-markup';
import { Params, useParams } from 'react-router';
import { Button, Card, Descriptions } from 'antd';
import { PRETTY_DATE_FORMAT } from '@owl-frontend/components';
import type { UnionExpand } from '@owl-lib/type-util';
import type {
  Activity,
  ActivityAttribute,
} from '../../../api/activities/interface';
import { Locale } from '../../../context/AppProvider';
import { SectionFeedbackComponent } from '../../Feedback/FeedbackComponents';
import styles from './ClaimActivitiesContainer.module.scss';

dayjs.extend(utc);
const MAX_HEIGHT = 290;
interface ActivityFilterProps {
  attributes: ActivityAttribute[];
  category: string;
  activityId: string;
  activityRefs?: MutableRefObject<Record<string, HTMLDivElement | null>>;
  hideBorder?: boolean;
  hideTitle?: boolean;
  removeMoreButton?: boolean;
  setSelectedActivity?: (activity: Activity) => void;
}

export const formatTitle = (title: string) => title;
export const formatDate = (date: string) =>
  dayjs(date).utc().format(PRETTY_DATE_FORMAT);

const ActivityCard: React.FC<ActivityFilterProps> = ({
  attributes,
  category,
  activityId,
  hideBorder,
  hideTitle,
  removeMoreButton = false,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isTooLarge, setIsTooLarge] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const { messages } = React.useContext(Locale);
  const { dossierId } = useParams() as UnionExpand<Params>;

  React.useEffect(() => {
    setIsExpanded(false);
    if (removeMoreButton) {
      setIsTooLarge(false);
      return;
    }

    const el = contentRef.current;
    if (el) {
      const observer = new ResizeObserver(() => {
        const shouldShowMore = el.scrollHeight > MAX_HEIGHT;
        setIsTooLarge(shouldShowMore);
      });
      observer.observe(el);
      return () => observer.disconnect();
    }
  }, [attributes, removeMoreButton]);

  if (!dossierId) {
    return null;
  }

  return (
    <Card
      title={
        hideTitle
          ? null
          : messages[`claim_overview.activities.activity_type.${category}`]
      }
      type="inner"
      className={clsx(styles.activityCard, { [styles.bordered]: !hideBorder })}
      extra={
        <SectionFeedbackComponent
          type="notes"
          dossier_id={dossierId}
          data={{ activity_id: activityId }}
        />
      }
    >
      <div
        ref={contentRef}
        style={{
          maxHeight: isExpanded ? 'none' : `${MAX_HEIGHT}px`,
          overflow: isExpanded ? 'visible' : 'hidden',
          position: 'relative',
          transition: 'max-height 0.3s ease',
        }}
      >
        {!isExpanded && isTooLarge && (
          <div
            style={{
              background:
                'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))',
              height: '60px',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              pointerEvents: 'none',
            }}
          />
        )}
        <Descriptions bordered column={1} size="small">
          {attributes
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            .map((attribute) => (
              <Descriptions.Item
                key={attribute.attribute_id}
                label={attribute.attribute_description}
                labelStyle={{
                  width: '200px',
                  whiteSpace: 'nowrap',
                  verticalAlign: 'top',
                }}
              >
                <Markup markup={attribute.attribute_text_html} />
              </Descriptions.Item>
            ))}
        </Descriptions>
      </div>

      {(isTooLarge || isExpanded) && (
        <div className={styles.expandButton}>
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded
              ? messages['claim_overview.activities.activity_expand.collapse']
              : messages['claim_overview.activities.activity_expand.expand']}
          </Button>
        </div>
      )}
    </Card>
  );
};

export default ActivityCard;
